import { FC, useCallback } from 'react';
import { Button } from '@simplywallst/ui-core';
import { useTracking } from 'react-tracking';
import { Wrapper } from './styled';

export interface ModalFooterProps {
  disabled?: boolean;
  justifyContent?: 'flex-end' | 'flex-start';
  confirmWithSubmit?: boolean;
  hideSecondary?: boolean;
  secondaryLabel?: string;
  hidePrimary?: boolean;
  primaryLabel?: string;
  primaryLoading?: boolean;
  secondaryLoading?: boolean;
  primaryDisabled?: boolean;
  secondaryDisabled?: boolean;
  onPrimaryClick?: (e: Event) => void;
  onSecondaryClick?: () => void;
}

const ModalFooter: FC<ModalFooterProps> = ({
  confirmWithSubmit = false,
  disabled = false,
  hideSecondary = false,
  secondaryLabel = '',
  onPrimaryClick,
  onSecondaryClick,
  hidePrimary = false,
  primaryLabel = '',
  primaryLoading = false,
  secondaryLoading = false,
  primaryDisabled = false,
  secondaryDisabled = false,
  justifyContent = 'flex-end',
  children,
}) => {
  const { trackEvent } = useTracking();

  const handleSecondaryClick = useCallback(() => {
    onSecondaryClick && onSecondaryClick();
    trackEvent({
      type: 'track',
      action: 'click',
      modifier: secondaryLabel,
    });
  }, [onSecondaryClick, secondaryLabel, trackEvent]);

  const handlePrimaryClick = useCallback(
    (e) => {
      confirmWithSubmit === false && onPrimaryClick && onPrimaryClick(e);
      trackEvent({
        type: 'track',
        action: 'click',
        modifier: primaryLabel,
      });
    },
    [confirmWithSubmit, onPrimaryClick, primaryLabel, trackEvent]
  );

  return (
    <Wrapper as="footer" justifyContentMd={justifyContent || 'flex-end'}>
      {children ? (
        children
      ) : (
        <>
          {hideSecondary !== true && (
            <Button
              type="button"
              styleType="gentleLargeReverse"
              marginRight
              displaySmDown="block"
              onClick={handleSecondaryClick}
              disabled={secondaryDisabled || disabled || secondaryLoading}
              isLoading={secondaryLoading}
              data-cy-id="modal-close-button"
            >
              {secondaryLabel}
            </Button>
          )}
          {hidePrimary !== true && (
            <Button
              styleType="louderLarge"
              type={confirmWithSubmit ? 'submit' : 'button'}
              displaySmDown="block"
              disabled={primaryDisabled || disabled || primaryLoading}
              onClick={handlePrimaryClick}
              isLoading={primaryLoading}
              data-cy-id="modal-confirm-button"
            >
              {primaryLabel}
            </Button>
          )}
        </>
      )}
    </Wrapper>
  );
};

export default ModalFooter;
